import React, { useState, useEffect } from 'react';
import { Retry } from '@components/common';
import { useSelector } from 'react-redux';
import { dimensionsListSelector } from '@store/common/dimensionList/selectors';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { CodeBlock } from './CodeBlock';
import { useFormattedData, useDimensions, useIsDecodedStackTraceEnabled } from './hooks';
import { StyledPanel, StyledSearch, StyledTable } from '../styled';
import { RECORD_KEYS, getColumns, getDimensionNames, getStackTraceFromData } from './utils';

const StackTracePanel = ({ dimension: originalDimension, data: defaultData, columns }) => {
  const dimensionList = useSelector(dimensionsListSelector);
  const alllowShowDecodedVersion = useIsDecodedStackTraceEnabled();
  const dimensions = useDimensions(originalDimension);

  const {
    data: formattedData,
    isError,
    isFetching,
    isEmpty,
    refetch,
  } = useFormattedData({
    enableFetchData: alllowShowDecodedVersion,
    dimensions,
    defaultData,
  });

  const [tableData, setTableData] = useState(formattedData);

  // Update table data once formatted data is ready
  useEffect(() => {
    if (formattedData?.length) {
      setTableData(formattedData);
    }
  }, [formattedData]);

  const [searchText, setSearchText] = useState('');
  const allColumns = getColumns(columns, dimensions, dimensionList);

  useEffect(() => {
    if (searchText && searchText.length) {
      const filteredData = formattedData.filter((d: any) => {
        return dimensions.some((it) => {
          const isStackTraceKey = it === RECORD_KEYS.ENCODED_STACK_TRACE;
          const dimensionValue = isStackTraceKey ? getStackTraceFromData(d) : d[it];
          return String(dimensionValue ?? '')
            .toLocaleLowerCase()
            .includes(searchText.toLowerCase());
        });
      });
      setTableData(filteredData);
    } else {
      setTableData(formattedData);
    }
  }, [formattedData, dimensions, searchText]);

  const onSearch = (e) => {
    const { value } = e.target;
    setSearchText(value);
  };

  const dimensionNames = getDimensionNames(dimensions, dimensionList);

  return (
    <StyledPanel>
      <Retry isLoading={isFetching} isEmpty={isEmpty} showError={isError} fetchData={refetch}>
        <StyledSearch
          allowClear
          placeholder={`Search for ${dimensionNames.join(', ')}`}
          width={200 + 80 * dimensionNames.length}
          onChange={onSearch}
        />
        <StyledTable
          columns={allColumns}
          expandable={{
            columnWidth: 50,
            expandRowByClick: true,
            expandedRowRender: (record: Record<string, any>, idx) => {
              return (
                <CodeBlock
                  data={record}
                  encodedStackTrace={record[RECORD_KEYS.ENCODED_STACK_TRACE]}
                  decodedStackTrace={record[RECORD_KEYS.DECODED_STACK_TRACE]}
                  isDecoded={record.isDecoded}
                  highlightText={searchText}
                  enableSwitch={alllowShowDecodedVersion}
                  onChange={(text, isDecoded) => {
                    tableData[idx].isDecoded = isDecoded;
                    if (isDecoded) {
                      tableData[idx][RECORD_KEYS.DECODED_STACK_TRACE] = text;
                    }
                    setTableData([...tableData]);
                  }}
                />
              );
            },
            rowExpandable: () => true,
            expandIcon: ({ expanded, onExpand, record }) => {
              return (
                <div onClick={(e) => onExpand(record, e)}>
                  {expanded ? <DownOutlined /> : <RightOutlined />}
                </div>
              );
            },
          }}
          rowKey={(record: any) => record.value}
          dataSource={tableData}
          pagination={false}
        />
      </Retry>
    </StyledPanel>
  );
};

export default StackTracePanel;
